import { Alert, Box, Button, HStack, IconButton, Stack, Table, Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { LuPencil, LuPlus, LuX } from 'react-icons/lu'
import { Tooltip } from '@/components/ui/tooltip'
import {
  formatEuro,
  formatIsoDateDE,
  isBaselineDate,
  type PlannedValue,
} from '@/helper/valueChange.helper'

export type ServicePriceTableRow = {
  /** Stable key, the service code. */
  key: string
  label: string
  /** The gross price in effect today; null when none is set yet. */
  activeValue: number | null
  /** Since when the active price has been in effect. */
  activeSince?: string | null
  /** Price changes scheduled for a later date, earliest first. */
  planned: PlannedValue[]
  /** The planned change currently loaded into the editor, if any. */
  editingId: number | null
  /** Whether this row's inline editor is open; `editor` is rendered below the row then. */
  editorOpen: boolean
  editor: ReactNode
  onPlan: () => void
  onEditPlanned: (planned: PlannedValue) => void
  onDropPlanned: (planned: PlannedValue) => void
  /** Offered for banks whose services the user may remove. */
  onRemove?: () => void
}

export type FixedPriceRow = {
  key: string
  label: string
  value: number | null
  onRemove?: () => void
}

type ServicePriceTableProps = {
  rows: ServicePriceTableRow[]
  /** Services whose price is fixed by contract: shown as a read-only notice, not as a row. */
  fixed?: FixedPriceRow[]
  disabled?: boolean
}

function activeSinceNote(activeSince: string | null | undefined) {
  if (!activeSince) return null
  return isBaselineDate(activeSince) ? 'von Anfang an' : `seit ${formatIsoDateDE(activeSince)}`
}

/**
 * The price section of "Auftraggeber bearbeiten": one row per service with the gross price in
 * effect, its planned change and the actions on it. Prices and dates are shown, never typed, until
 * the user plans or edits a change - the editor the dialog passes in then opens under the row.
 * Structure only: the dialog owns the form state and the save.
 */
export function ServicePriceTable({ rows, fixed = [], disabled = false }: ServicePriceTableProps) {
  return (
    <Stack gap={4}>
      {fixed.length > 0 && (
        <Alert.Root status="neutral" variant="subtle" data-testid="fixed-price-notice">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>
              {fixed.map(f => (
                <HStack key={f.key} gap={2} flexWrap="wrap">
                  <Text as="span">
                    {f.label}: Festpreis {formatEuro(f.value)} brutto, nicht änderbar.
                  </Text>
                  {f.onRemove && (
                    <Button
                      variant="ghost"
                      onClick={f.onRemove}
                      disabled={disabled}
                      aria-label={`Leistung ${f.label} entfernen`}
                    >
                      Entfernen
                    </Button>
                  )}
                </HStack>
              ))}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}

      <Box overflowX="auto">
        <Table.Root size="sm" minW="640px" data-testid="service-price-table">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Leistung</Table.ColumnHeader>
              <Table.ColumnHeader>Aktueller Preis (brutto)</Table.ColumnHeader>
              <Table.ColumnHeader>Geplante Änderung</Table.ColumnHeader>
              <Table.ColumnHeader>
                <Text as="span" srOnly>
                  Aktionen
                </Text>
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map(row => (
              <ServicePriceRow key={row.key} row={row} disabled={disabled} />
            ))}
          </Table.Body>
        </Table.Root>
      </Box>
    </Stack>
  )
}

function ServicePriceRow({ row, disabled }: { row: ServicePriceTableRow; disabled: boolean }) {
  const { label } = row
  const since = activeSinceNote(row.activeSince)

  return (
    <>
      <Table.Row data-testid={`service-price-row-${row.key}`}>
        <Table.Cell>{label}</Table.Cell>

        <Table.Cell>
          {row.activeValue === null ? (
            <Text color="fg.muted">Noch kein Preis hinterlegt</Text>
          ) : (
            <Stack gap={0}>
              <Text as="span" data-testid={`active-value-${label}`}>
                {formatEuro(row.activeValue)} / Einheit
              </Text>
              {since && <Text color="fg.muted">({since})</Text>}
            </Stack>
          )}
        </Table.Cell>

        <Table.Cell>
          {row.planned.length === 0 ? (
            <Text color="fg.muted">Keine geplante Änderung</Text>
          ) : (
            <Stack gap={1}>
              {row.planned.map(change => (
                <Text key={change.id} as="span" data-testid={`planned-value-${label}`}>
                  Geplant: {formatEuro(change.value)} ab {formatIsoDateDE(change.validFrom)}
                  {row.editingId === change.id && (
                    <>
                      {' '}
                      <Text as="span" color="fg.muted">
                        (wird bearbeitet)
                      </Text>
                    </>
                  )}
                </Text>
              ))}
            </Stack>
          )}
        </Table.Cell>

        <Table.Cell>
          <HStack gap={2} flexWrap="wrap" justify="flex-end">
            {row.planned.length === 0 && !row.editorOpen && (
              <Button variant="plain" onClick={row.onPlan} disabled={disabled}>
                <LuPlus />
                {row.activeValue === null ? 'Preis festlegen' : 'Preisänderung planen'}
              </Button>
            )}
            {row.planned.map(change => (
              <HStack key={change.id} gap={2}>
                <Tooltip content="Bearbeiten">
                  <IconButton
                    variant="outline"
                    aria-label="Bearbeiten"
                    onClick={() => row.onEditPlanned(change)}
                    disabled={disabled || row.editingId === change.id}
                  >
                    <LuPencil />
                  </IconButton>
                </Tooltip>
                <Tooltip content="Geplante Preisänderung verwerfen">
                  <IconButton
                    variant="outline"
                    colorPalette="red"
                    aria-label="Geplante Preisänderung verwerfen"
                    onClick={() => row.onDropPlanned(change)}
                    disabled={disabled}
                  >
                    <LuX />
                  </IconButton>
                </Tooltip>
              </HStack>
            ))}
            {row.onRemove && (
              <Button
                variant="ghost"
                onClick={row.onRemove}
                disabled={disabled}
                aria-label={`Leistung ${label} entfernen`}
              >
                Entfernen
              </Button>
            )}
          </HStack>
        </Table.Cell>
      </Table.Row>

      {row.editorOpen && (
        <Table.Row data-testid={`service-price-editor-${row.key}`}>
          <Table.Cell colSpan={4}>
            <Box role="group" aria-label={`Preisänderung ${label}`}>
              {row.editor}
            </Box>
          </Table.Cell>
        </Table.Row>
      )}
    </>
  )
}
